import React, { useState } from 'react';
import styled from 'styled-components';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { SmallButton } from '../onboarding/review/contractReviewStyles';
import { Card, CardHead, INK, Input } from '../onboarding/review/reviewStyles';
import { fmtMoney, fmtSigned, humanizeToken, plural } from '../presentation/format';
import { disposeVariance, type CollectionPeriod, type VarianceLine } from './periodApi';

/**
 * Certificate comparison (F-LR-048): every line the comparison wrote, not only the ones that
 * differ. A matched line is evidence too — the reader sees that eligible, ineligible, reserve and
 * the base were all checked, and which of them agreed. Variance first, matched muted, and the
 * disposition controls only where there is something to decide.
 */

interface Props {
  period: CollectionPeriod;
  facilityId: number;
  currency?: string | null;
  busy: string | null;
  run: (key: string, action: () => Promise<unknown>, success: string) => Promise<boolean>;
}

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid ${INK.line}; vertical-align: top; }
  th { color: ${INK.faint}; font-size: 11px; font-weight: 750; letter-spacing: .06em; text-transform: uppercase; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tr[data-blocking='true'] td:first-child { border-left: 3px solid #f0a24a; }
  tr[data-accepted='true'] td { color: ${INK.muted}; }
  tr[data-matched='true'] td { color: ${INK.muted}; }
  tr[data-matched='true'] td.num { color: ${INK.soft}; }
  tbody tr:hover { background: rgba(56,189,248,.04); }
  small { color: ${INK.faint}; }
`;

const SUMMARY_ORDER = ['ELIGIBLE', 'INELIGIBLE', 'RESERVE', 'BORROWING_BASE'];
const SUMMARY_LABELS: Record<string, string> = {
  ELIGIBLE: 'Eligible collateral', INELIGIBLE: 'Ineligible collateral', RESERVE: 'Reserve', BORROWING_BASE: 'Borrowing base',
};

export type ComparisonStatusKind = 'MATCHED' | 'WITHIN_TOLERANCE' | 'VARIANCE' | 'MISSING';

/** The four words a line can wear; MISSING_REPORTED, MISSING_COMPUTED and MISSING_ASSET all read as missing. */
export const comparisonStatusKind = (status: string | null | undefined): ComparisonStatusKind => {
  const s = String(status || '').toUpperCase();
  if (s === 'MATCHED') return 'MATCHED';
  if (s === 'WITHIN_TOLERANCE') return 'WITHIN_TOLERANCE';
  if (s.startsWith('MISSING')) return 'MISSING';
  return 'VARIANCE';
};

const STATUS_WORDS: Record<ComparisonStatusKind, string> = {
  MATCHED: 'Matched', WITHIN_TOLERANCE: 'Within tolerance', VARIANCE: 'Variance', MISSING: 'Missing',
};
const statusTone = (kind: ComparisonStatusKind, blocking: boolean): StatusTone =>
  kind === 'MATCHED' ? 'done' : kind === 'WITHIN_TOLERANCE' ? 'progress' : blocking ? 'critical' : 'warning';

const missingWords = (status: string) => status === 'MISSING_REPORTED' ? 'Not on the certificate'
  : status === 'MISSING_COMPUTED' ? 'Not computed' : status === 'MISSING_ASSET' ? 'Asset not on the tape' : 'Missing';

/** "Eligible collateral" for SUMMARY:ELIGIBLE; the humanised key plus the asset for a loan-level line. */
export const comparisonLineLabel = (line: Pick<VarianceLine, 'line_key' | 'asset_identifier'>): string => {
  const key = String(line.line_key || '');
  const summary = key.startsWith('SUMMARY:') ? key.slice('SUMMARY:'.length) : null;
  const base = summary ? (SUMMARY_LABELS[summary] || humanizeToken(summary)) : humanizeToken(key);
  return line.asset_identifier ? `${base} · ${line.asset_identifier}` : base;
};

const rank = (line: VarianceLine) => {
  const kind = comparisonStatusKind(line.status);
  const group = kind === 'MATCHED' ? 3 : kind === 'WITHIN_TOLERANCE' ? 2 : line.blocking && !line.disposition ? 0 : 1;
  const key = String(line.line_key || '').replace('SUMMARY:', '');
  const summaryIndex = SUMMARY_ORDER.indexOf(key);
  return group * 100 + (summaryIndex === -1 ? 50 : summaryIndex);
};

/** Variance and missing lines first (blocking at the top), then within tolerance, then matched; summary lines in base order. */
export const orderComparisonLines = (lines: VarianceLine[]): VarianceLine[] =>
  [...lines].sort((a, b) => rank(a) - rank(b) || a.id - b.id);

const ComparisonCard: React.FC<Props> = ({ period, facilityId, currency, busy, run }) => {
  const c = period.comparison!;
  const [reasons, setReasons] = useState<Record<number, string>>({});
  const decided = period.run?.approvalStatus === 'APPROVED';
  const lines = orderComparisonLines(c.lines || []);
  const matched = lines.filter(l => comparisonStatusKind(l.status) === 'MATCHED').length;
  const differing = lines.length - matched;
  const dispose = (line: VarianceLine, disposition: 'ACCEPTED' | 'CORRECTED' | 'ESCALATED') =>
    run(`line-${line.id}`, () => disposeVariance(facilityId, c.id, line.id, disposition, reasons[line.id] || ''), `Variance ${disposition.toLowerCase()}`);
  const thresholds = c.thresholdSource === 'CHECKER_OVERRIDE' ? 'overridden' : c.thresholdSource === 'FACILITY_POLICY' ? 'from the facility policy' : 'from the tenant policy';
  const ccy = currency || 'USD';
  return <Card style={{ marginTop: 14 }}>
    <CardHead>
      <div><h2>Certificate comparison</h2>
        <p>{c.varianceWords} · {lines.length === 0 ? 'no lines compared' : matched === lines.length ? `all ${plural(lines.length, 'line')} match` : `${plural(differing, 'line')} differ, ${matched} match`} · thresholds {thresholds}: tolerance {fmtMoney(c.toleranceAmount, ccy)}, material {fmtMoney(c.materialityAmount, ccy)}, blocking {fmtMoney(c.blockingAmount, ccy)}</p></div>
      <StatusChip tone={c.varianceBlocking ? 'critical' : c.materialVariance ? 'warning' : 'done'} label={c.varianceBlocking ? 'Blocking' : c.materialVariance ? 'Material' : 'Clear'} />
    </CardHead>
    {lines.length === 0 ? <p style={{ color: INK.muted }}>The comparison wrote no lines. Record the certificate's eligible, ineligible and reserve figures to compare them with the computation.</p> : <Table>
      <thead><tr><th>Line</th><th>Status</th><th className="num">Certificate</th><th className="num">Computed</th><th className="num">Variance</th><th>Decision</th></tr></thead>
      <tbody>{lines.map(l => {
        const kind = comparisonStatusKind(l.status);
        const open = kind !== 'MATCHED';
        return <tr key={l.id} data-blocking={l.blocking && !l.disposition} data-accepted={l.disposition === 'ACCEPTED'} data-matched={!open}>
          <td>{comparisonLineLabel(l)}{l.explanation && open ? <><br /><small>{l.explanation}</small></> : null}</td>
          <td><StatusChip tone={statusTone(kind, l.blocking)} label={kind === 'MISSING' ? missingWords(l.status) : STATUS_WORDS[kind]} /></td>
          <td className="num">{fmtMoney(l.reported_value, ccy)}</td>
          <td className="num">{fmtMoney(l.computed_value, ccy)}</td>
          <td className="num" style={{ color: l.blocking && !l.disposition ? '#f0a24a' : undefined }}>{open ? fmtSigned(l.variance_amount, { currency: ccy }) : fmtMoney(0, ccy)}</td>
          <td>{!open ? <span style={{ color: INK.faint }}>No decision needed</span>
            : <>
              {l.carried_from_line_id && !l.disposition && <small>Previously accepted on an earlier comparison; review again.</small>}
              {l.disposition ? <span>{l.disposition.toLowerCase()} by {l.disposed_by} — “{l.disposition_reason}”{l.carried_from_line_id ? ' (carried forward)' : ''}</span>
                : decided ? <span style={{ color: INK.faint }}>Approved without a disposition</span>
                  : <div style={{ display: 'grid', gap: 6 }}>
                    <Input value={reasons[l.id] || ''} placeholder="Reason" onChange={e => setReasons({ ...reasons, [l.id]: e.target.value })} aria-label={`Reason for ${l.line_key}`} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <SmallButton type="button" disabled={!period.readiness.actorMayCheck || busy !== null || !(reasons[l.id] || '').trim()} onClick={() => void dispose(l, 'ACCEPTED')}>Accept</SmallButton>
                      <SmallButton type="button" disabled={!period.readiness.actorMayCheck || busy !== null || !(reasons[l.id] || '').trim()} onClick={() => void dispose(l, 'CORRECTED')}>Needs correction</SmallButton>
                      <SmallButton type="button" disabled={!period.readiness.actorMayCheck || busy !== null || !(reasons[l.id] || '').trim()} onClick={() => void dispose(l, 'ESCALATED')}>Escalate</SmallButton>
                    </div>
                  </div>}
            </>}</td>
        </tr>;
      })}</tbody>
    </Table>}
  </Card>;
};

export default ComparisonCard;
